import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MyBusinessSchema, ResourceSchema } from '../data/types';
import { useAllBusinesses } from '../hooks/businesshooks';
import { dscvryOpenUrl } from '../utils/contactBuilder';

type DscvryBusinessSelectProps = {
  uid?: string
  businessId: string
  onChange: (id: string) => void
  hubUrl: string
};

/** Business picker and DSCVRY hub link. Setup of target customers stays in DSCVRY. */
export const DscvryBusinessSelect = ({ uid, businessId, onChange, hubUrl }: DscvryBusinessSelectProps) => {
  const { businesses, isLoadingAllBusinesses } = useAllBusinesses(uid || '');
  const rows = (businesses || []) as MyBusinessSchema[];
  const selected = useMemo(() => rows.find((item) => item.id === businessId), [rows, businessId]);

  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="font-raleway font-semibold text-ink">Business</span>
        <a href={hubUrl} target="_blank" rel="noreferrer" className="font-raleway text-xs font-semibold text-primaryDark-0 hover:underline">
          Open the DSCVRY Business Hub
        </a>
      </div>
      {!uid && <p className="text-[#5B7480]">Sign in to choose a business.</p>}
      {uid && isLoadingAllBusinesses && <p className="text-[#5B7480]">Loading your businesses.</p>}
      {uid && !isLoadingAllBusinesses && rows.length === 0 && (
        <p className="text-[#5B7480]">
          Add a business in <Link to="/business" className="font-semibold text-primaryDark-0">My Business</Link> before generating a list.
        </p>
      )}
      {uid && rows.length > 0 && (
        <label className="block">
          <span className="sr-only">Business</span>
          <select
            className="w-full rounded-lg border border-[#E4EEF1] bg-white p-2"
            value={businessId}
            onChange={(event) => onChange(event.target.value)}
            data-testid="dscvry-business"
          >
            <option value="">Select a business</option>
            {rows.map((item) => (
              <option key={item.id} value={item.id}>{item.business_name}</option>
            ))}
          </select>
        </label>
      )}
      {selected && (
        <p className="truncate text-xs text-[#5B7480]" data-testid="dscvry-business-profile" title={selected.description || undefined}>
          {[selected.business_category, selected.cooking_city, selected.cooking_state].filter(Boolean).join(' · ') || selected.business_name}
        </p>
      )}
    </div>
  );
};

type DscvrySchedulePanelProps = {
  resource: ResourceSchema
  uid?: string
  onClose: () => void
};

/** Replaces the empty expert-session picker for a DSCVRY listing. */
export const DscvrySchedulePanel = ({ resource, uid, onClose }: DscvrySchedulePanelProps) => {
  const [params, setSearchParams] = useSearchParams();
  const [businessId, setBusinessId] = useState(params.get('business') || '');

  const build = () => {
    const next = new URLSearchParams(params);
    if (businessId) next.set('business', businessId);
    setSearchParams(next, { replace: true });
    onClose();
    window.setTimeout(() => {
      document.getElementById('contact-list-builder')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  return (
    <div className="w-11/12 pb-6 text-left" data-testid="dscvry-schedule">
      <p className="font-raleway text-[22px] font-medium text-black">Build a contact list</p>
      <p className="my-2 font-open-sans text-[15px] text-[#333333]">
        Choose the pantry business, then finish target customers in DSCVRY. This listing does not use a session calendar.
      </p>
      <DscvryBusinessSelect
        uid={uid}
        businessId={businessId}
        onChange={setBusinessId}
        hubUrl={dscvryOpenUrl(resource)}
      />
      <button
        type="button"
        className="mt-4 rounded-lg border-0 bg-primaryDark-0 px-4 py-2 font-raleway text-white disabled:opacity-50"
        onClick={build}
        disabled={!businessId}
      >
        Build list
      </button>
    </div>
  );
};
